import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Shop.css';
import NavBarTop from '../components/NavBarTop';
import BottomSheet from '../components/BottomSheet';
import '../components/BottomSheet.css';

const Shop = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [totalProducts, setTotalProducts] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    const BASE_URL = 'https://physiodirectory.in';

    // Removed initial mount fetch to prevent double loading. All fetches handled by filter effect below.

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line
    }, [searchTerm, selectedCategory]);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const params = { page };
            if (searchTerm) params.search = searchTerm;
            if (selectedCategory) params.category = selectedCategory;

            const response = await axios.get(`${BASE_URL}/api/shop`, { params });
            
            setProducts(response.data.products.data);
            setTotalProducts(response.data.products.total);
            setCategories(response.data.categories);
            setCurrentPage(response.data.products.current_page);
            setLastPage(response.data.products.last_page);
        } catch (error) {
            console.error('Error fetching shop data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            fetchData(newPage);
            window.scrollTo(0, 0);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleCategoryClick = (categoryId) => {
        setSelectedCategory(categoryId === selectedCategory ? '' : categoryId);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
    };

    const handleBuyNow = (url) => {
        if (!url) return;
        if (window.Native && window.Native.openExternal) {
            window.Native.openExternal({ url });
        } else {
            window.open(url, '_blank');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    return (
        <div className="mobile-page-container">
            {/* Top NavBar with Back and Title and Filter Button */}
            <NavBarTop 
                title="Shop"
                right={
                    <button className="btn btn-link p-0" style={{fontSize: '1.5rem'}} onClick={() => setFilterSheetOpen(true)}>
                        <i className="bi bi-funnel"></i>
                    </button>
                }
            />
            <BottomSheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} className="shop-bottom-sheet">
                <form onSubmit={e => e.preventDefault()} className="mb-3">
                    <label className="form-label fw-bold">Search</label>
                    <div className="search-input-wrapper">
                        <i className="bi bi-search"></i>
                        <input 
                            type="text" 
                            className="form-control modern-search-input" 
                            placeholder="Search Products..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </form>
                <label className="form-label fw-bold">Category</label>
                <div className="category-scroll-container mt-2 mb-0 ps-0">
                    <div 
                        className={`category-pill ${selectedCategory === '' ? 'active' : ''} no-focus-outline`}
                        onClick={() => setSelectedCategory('')}
                    >
                        All
                    </div>
                    {categories.map(cat => (
                        <div 
                            key={cat.id}
                            className={`category-pill ${selectedCategory === cat.id ? 'active' : ''} no-focus-outline`}
                            onClick={() => handleCategoryClick(cat.id)}
                        >
                            {cat.name}
                        </div>
                    ))}
                </div>
            </BottomSheet>

            <div className="container-fluid px-3 pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-dark">Fresh Arrivals</h5>
                    <span className="text-muted small">{totalProducts} Items</span>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                         <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <div className="row g-3">
                        {products.length > 0 ? (
                            products.map(product => {
                                const hasOffer = product.offer_price && Number(product.offer_price) < Number(product.normal_price);
                                const discount = hasOffer ? Math.round(((Number(product.normal_price) - Number(product.offer_price)) / Number(product.normal_price)) * 100) : 0;
                                
                                return (
                                <div className="col-6 col-md-4 col-lg-3" key={product.id}>
                                    <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden product-card d-flex flex-column">
                                        <div className="position-relative" style={{ paddingTop: '100%' }}>
                                            {hasOffer && (
                                                <span className="position-absolute top-0 end-0 m-2 badge bg-danger rounded-pill" style={{zIndex: 2, fontSize: '0.65rem'}}>
                                                    {discount}% OFF
                                                </span>
                                            )}
                                            <img 
                                                src={product.image_url || (product.image ? `${BASE_URL}/storage/${product.image}` : 'https://placehold.co/400x400?text=No+Image')} 
                                                alt={product.name}
                                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x400?text=No+Image'; }}
                                                className="img-fluid position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
                                            />
                                        </div>
                                        <div className="card-body p-3 d-flex flex-column flex-grow-1">
                                            <div className="mb-1">
                                                 <span className="badge bg-light text-secondary border fw-normal" style={{fontSize: '0.65rem'}}>
                                                    {product.category?.name || 'General'}
                                                </span>
                                            </div>
                                            <h6 className="card-title fw-bold text-dark mb-1 text-truncate-2" style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                                                {product.name}
                                            </h6>
                                            {product.description && (
                                                <p className="text-muted small mb-2 text-truncate-2" style={{ fontSize: '0.75rem', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {product.description}
                                                </p>
                                            )}
                                            <div className="mt-auto pt-2">
                                                <div className="mb-2">
                                                    {hasOffer ? (
                                                        <div className="d-flex align-items-center flex-wrap">
                                                            <span className="fw-bold text-primary me-2" style={{ fontSize: '1rem' }}>
                                                                {formatCurrency(product.offer_price)}
                                                            </span>
                                                            <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.75rem' }}>
                                                                {formatCurrency(product.normal_price)}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="fw-bold text-primary" style={{ fontSize: '1rem' }}>
                                                            {formatCurrency(product.normal_price)}
                                                        </span>
                                                    )}
                                                </div>
                                                <button 
                                                    onClick={() => handleBuyNow(product.buy_link)}
                                                    className="btn btn-sm w-100 btn-primary rounded-3 fw-bold" 
                                                    style={{fontSize: '0.8rem'}}
                                                >
                                                    <i className="bi bi-cart-plus me-1"></i> Buy Now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })
                        ) : (
                            <div className="col-12 text-center py-5">
                                 <i className="bi bi-bag-x display-1 text-light"></i>
                                 <p className="text-muted mt-3">No products found.</p>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Pagination */}
                {!loading && lastPage > 1 && (
                    <div className="d-flex justify-content-center align-items-center mt-4 mb-5 pb-5 gap-3">
                        <button 
                            className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === 1 ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                            style={{width: '40px', height: '40px'}}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="fw-bold text-dark mx-2 small">
                            Page {currentPage} of {lastPage}
                        </span>
                        <button 
                            className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === lastPage ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                            style={{width: '40px', height: '40px'}}
                            disabled={currentPage === lastPage}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                             <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Shop;
